if ( typeof TagSlider !== 'function' ) {

  class TagSlider extends HTMLElement {

    constructor(){

      super();

      // references to the markup that already lives in the liquid
      this.track = this.querySelector('.blog-tags');
      this.scrollbar = this.querySelector('.tag-slider__scrollbar');
      this.thumb = this.querySelector('.tag-slider__thumb');

    }

    connectedCallback(){

      if ( ! this.track || ! this.scrollbar || ! this.thumb ) {
        return;
      }

      // recalculate the scrollbar on resize (debounced) so it stays in sync
      // with the available width. debounce mirrors the pattern in component-slider.js
      this.RESIZE_EVENT = debounce(()=>{
        this.updateScrollbar();
      }, 100);
      window.addEventListener('resize', this.RESIZE_EVENT);

      // keep the thumb in sync while the strip is scrolled by any means
      this.track.addEventListener('scroll', ()=>{
        this.updateThumb();
      }, {passive:true});

      this.enableTrackDragging();
      this.enableThumbDragging();
      this.enableKeyboard();

      this.updateScrollbar();

    }

    // shows the scrollbar only when the tags overflow, and refreshes the thumb

    updateScrollbar(){

      const overflowing = this.track.scrollWidth > this.track.clientWidth + 1;
      this.scrollbar.hidden = ! overflowing;
      if ( overflowing ) {
        this.updateThumb();
      }

    }

    // thumb width reflects the visible ratio, position reflects the scroll ratio

    updateThumb(){

      const visibleRatio = this.track.clientWidth / this.track.scrollWidth;
      const scrollableWidth = this.track.scrollWidth - this.track.clientWidth;
      const scrollRatio = scrollableWidth > 0 ? this.track.scrollLeft / scrollableWidth : 0;

      this.thumb.style.width = ( visibleRatio * 100 ) + '%';
      this.thumb.style.left = ( scrollRatio * ( 100 - visibleRatio * 100 ) ) + '%';

      this.thumb.setAttribute('aria-valuemin', '0');
      this.thumb.setAttribute('aria-valuemax', '100');
      this.thumb.setAttribute('aria-valuenow', Math.round(scrollRatio * 100));

    }

    // drag the tag strip with the mouse. touch devices already scroll the
    // strip natively via overflow-x, so we only wire up mouse dragging here
    // to avoid fighting the native touch scroll (same approach as component-slider.js)

    enableTrackDragging(){

      let pointerActive = false;
      let startX = 0;
      let startScrollLeft = 0;

      this.track.addEventListener('pointerdown', e=>{
        if ( e.pointerType !== 'mouse' ) {
          return;
        }
        pointerActive = true;
        startX = e.clientX;
        startScrollLeft = this.track.scrollLeft;
        this.track.classList.add('is-grabbing');
      });

      this.track.addEventListener('pointermove', e=>{
        if ( ! pointerActive ) {
          return;
        }
        this.track.scrollLeft = startScrollLeft - ( e.clientX - startX );
      });

      const endDrag = ()=>{
        pointerActive = false;
        this.track.classList.remove('is-grabbing');
      };
      this.track.addEventListener('pointerup', endDrag);
      this.track.addEventListener('pointerleave', endDrag);
      this.track.addEventListener('pointercancel', endDrag);

    }

    // drag the thumb itself to scroll the strip (bidirectional coupling)

    enableThumbDragging(){

      let pointerActive = false;
      let startX = 0;
      let startScrollLeft = 0;

      this.thumb.addEventListener('pointerdown', e=>{
        e.preventDefault();
        pointerActive = true;
        startX = e.clientX;
        startScrollLeft = this.track.scrollLeft;
        this.thumb.setPointerCapture(e.pointerId);
        this.thumb.classList.add('is-grabbing');
      });

      this.thumb.addEventListener('pointermove', e=>{
        if ( ! pointerActive ) {
          return;
        }
        // translate thumb travel into strip scroll distance
        const scrollableWidth = this.track.scrollWidth - this.track.clientWidth;
        const thumbTravel = this.scrollbar.clientWidth - this.thumb.clientWidth;
        if ( thumbTravel <= 0 ) {
          return;
        }
        this.track.scrollLeft = startScrollLeft + ( e.clientX - startX ) * ( scrollableWidth / thumbTravel );
      });

      const endDrag = e=>{
        pointerActive = false;
        this.thumb.classList.remove('is-grabbing');
      };
      this.thumb.addEventListener('pointerup', endDrag);
      this.thumb.addEventListener('pointercancel', endDrag);

    }

    // arrow keys scroll the strip when the thumb has focus

    enableKeyboard(){

      this.thumb.addEventListener('keydown', e=>{
        const step = this.track.clientWidth * 0.5;
        if ( e.key === 'ArrowRight' ) {
          e.preventDefault();
          this.track.scrollLeft += step;
        } else if ( e.key === 'ArrowLeft' ) {
          e.preventDefault();
          this.track.scrollLeft -= step;
        } else if ( e.key === 'Home' ) {
          e.preventDefault();
          this.track.scrollLeft = 0;
        } else if ( e.key === 'End' ) {
          e.preventDefault();
          this.track.scrollLeft = this.track.scrollWidth;
        }
      });

    }

  }

  if ( typeof customElements.get('tag-slider') == 'undefined' ) {
    customElements.define('tag-slider', TagSlider);
  }

  // reinitialise inside the theme editor when the section is reloaded
  document.addEventListener('shopify:section:load', (e)=>{
    e.target.querySelectorAll('tag-slider').forEach(slider=>{
      if ( slider.updateScrollbar ) {
        slider.updateScrollbar();
      }
    });
  });

}
